import { loadFlowGuideSteps, toBodyHtml, type GuideStep } from "../loadProjectOverviewGuideSteps";
import { libraryExistingUserSteps } from "../steps/libraryExistingUserSteps";
import { libraryNewUserSteps } from "../steps/libraryNewUserSteps";
import { LIBRARY_WELCOME_FLOW_KEY } from "../welcomeFlow.config";
import { replayGuideStep } from "./replayGuideStep";
import type { WelcomeDeckWithFlow } from "./WelcomeDeck";

// FilterBox only sets this id on the materialsDatabase route (FilterLibraryMaterial.vue), which is
// the only route this deck ever runs on.
const FILTER_ANCHOR_ID = "filter-btn";
// The "New" button (CreateLibraryItemMenu.vue) — the deck's own ending, a plain Finish.
const ENDING_ANCHOR_ID = "create-material-construction-btn";

// Not Carbon0 rows — the toolbar controls the Library guide doesn't cover. Walked left to right,
// right before Filter (Filter itself is relocated here from wherever the guide rows placed it).
const LOCAL_TOOLBAR_STEPS: GuideStep[] = [
  {
    id: "",
    key: "library-type-tabs",
    anchorId: "library-type-tabs",
    title: "Materials and constructions",
    bodyHtml: toBodyHtml(
      "Switch between single materials and the constructions built from them. Search and filters follow the tab you are on."
    ),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "library-search",
    anchorId: "library-search",
    title: "Search",
    bodyHtml: toBodyHtml("Type part of a material's name to narrow the list below. Results update as you type."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "library-allowed-epds",
    anchorId: "library-allowed-epds",
    title: "Allowed EPDs",
    bodyHtml: toBodyHtml(
      "Limit the library to the EPDs accepted for your country's regulation, or open it up to every data source."
    ),
    alreadySeen: false,
    highlight: true,
  },
];

// The guide's own priority puts the Materials list first, ahead of Filter — the toolbar reads left
// to right (tabs, search, allowed EPDs, filter) before the list, so Filter is relocated to sit right
// after the local toolbar steps; every other row keeps its own relative order. When Filter is
// missing altogether, the toolbar steps go right before the ending row instead.
async function loadLibraryDeckSteps(): Promise<GuideStep[]> {
  const steps = await loadFlowGuideSteps("Library");
  const filterIndex = steps.findIndex((step) => step.anchorId === FILTER_ANCHOR_ID);

  if (filterIndex === -1) {
    const endingIndex = steps.findIndex((step) => step.anchorId === ENDING_ANCHOR_ID);
    if (endingIndex === -1) return [...steps, ...LOCAL_TOOLBAR_STEPS, replayGuideStep("library-nav-link")];
    return [
      ...steps.slice(0, endingIndex),
      ...LOCAL_TOOLBAR_STEPS,
      ...steps.slice(endingIndex),
      replayGuideStep("library-nav-link"),
    ];
  }

  const filterStep = steps[filterIndex];
  const otherSteps = steps.filter((_, index) => index !== filterIndex);
  return [...LOCAL_TOOLBAR_STEPS, filterStep, ...otherSteps, replayGuideStep("library-nav-link")];
}

export const libraryDeck: WelcomeDeckWithFlow = {
  id: "library",
  flowKey: LIBRARY_WELCOME_FLOW_KEY,
  intro: {
    new: libraryNewUserSteps[0],
    existing: libraryExistingUserSteps[0],
  },
  loadSteps: loadLibraryDeckSteps,
  ending: {
    anchorId: ENDING_ANCHOR_ID,
    // Always a plain Finish — nowhere for the library tour to hand the user off to.
    ctaLabel: () => undefined,
    runCta: () => {},
  },
};
